import { useState, useEffect } from 'react';
import type { Position } from '../types';
import { reverseGeocode, formatGeocodeResult } from '../utils/addressResolver';

/**
 * Hook to manage address resolution for a specific position
 * Handles loading state and caching
 * @param position The position to resolve
 * @param existingAddress Pre-resolved address if available
 * @param skipGeocoding Whether to skip geocoding entirely (for API optimization)
 */
export const useAddress = (position: Position | null, existingAddress?: string, skipGeocoding: boolean = false) => {
  const [address, setAddress] = useState<string | null>(existingAddress || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!position) {
      setAddress(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // If we already have an address (from parent component), use it
    if (existingAddress) {
      setAddress(existingAddress);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Skip geocoding if requested (for API optimization)
    if (skipGeocoding) {
      setAddress(`${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`);
      setIsLoading(false);
      setError(null);
      return;
    }

    const resolveAddress = async () => {
      // Don't show loading state for reverse geocoding to avoid dual loading spinners
      // The reverse geocoding results are mainly for background data storage
      setError(null);
      
      try {
        const geocodeResult = await reverseGeocode(position);
        const resolvedAddress = formatGeocodeResult(geocodeResult);
        setAddress(resolvedAddress);
      } catch (err) {
        console.error('Address resolution failed:', err);
        setError('Address resolution failed:');
        // Fallback to coordinates
        setAddress(`${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`);
      }
      // No setIsLoading(false) needed since we never set it to true
    };

    resolveAddress();
  }, [position?.lat, position?.lng, existingAddress, skipGeocoding]); // Include skipGeocoding in deps

  return { address, isLoading, error };
};
